/**
 * Regras da Contribuição Básica (percentual sobre o salário) por plano.
 *
 * As regras são indexadas pelo id do plano (claim `plan` do accessToken), os mesmos ids de
 * core/constants/planos.ts. O rótulo (planDescription) não é usado porque pode vir com
 * grafia diferente no token.
 *
 * ATENÇÃO: os valores de UP (Unidade Previdenciária) abaixo podem estar
 * desatualizados. Atualize-os aqui quando houver reajuste.
 */
export const PLANO = {
  CD2_ELETROPAULO: 22,
  CD_CPFL: 23,
  ISA_ENERGIA_BRASIL_PREV: 26,
  CESP_CD: 33,
} as const;

/** Valor da UP de cada plano, pelo id do plano. */
export const VALOR_UP: Readonly<Record<number, number>> = {
  [PLANO.ISA_ENERGIA_BRASIL_PREV]: 6602.06,
  [PLANO.CD_CPFL]: 4360.88,
  [PLANO.CD2_ELETROPAULO]: 3401.54,
};

export interface LimitesContribuicao {
  /** Percentual mínimo permitido (ex.: 0.5 = 0,5%). */
  minimo: number;
  /** Percentual máximo permitido. */
  maximo: number;
  /** Incremento aceito (ex.: 0.5 aceita 0,5 → 1 → 1,5). */
  escala: number;
}

/** Contribuição Voluntária: percentual livre de 0% a 100%, com duas casas. */
export const VOLUNTARIA: LimitesContribuicao = { minimo: 0, maximo: 100, escala: 0.01 };

/** Suspensão temporária das contribuições: de 1 a 12 meses. */
export const MESES_SUSPENSAO = { minimo: 1, maximo: 12 } as const;

/** Faixa salarial: vale para salários até `ateUps` × UP (inclusive). `null` = sem teto. */
interface FaixaSalarial {
  ateUps: number | null;
  minimo: number;
  maximo: number;
}

interface RegraPlano {
  /** Valor da UP do plano; `null` quando o salário não interfere na regra. */
  valorUp: number | null;
  escala: number;
  faixas: FaixaSalarial[];
}

export const REGRAS_CONTRIBUICAO_BASICA: Readonly<Record<number, RegraPlano>> = {
  [PLANO.ISA_ENERGIA_BRASIL_PREV]: {
    valorUp: VALOR_UP[PLANO.ISA_ENERGIA_BRASIL_PREV],
    escala: 0.01,
    faixas: [
      { ateUps: 1, minimo: 0, maximo: 4 },
      { ateUps: 2, minimo: 0, maximo: 5 },
      { ateUps: 4, minimo: 0, maximo: 6 },
      { ateUps: null, minimo: 0, maximo: 9 },
    ],
  },
  [PLANO.CESP_CD]: {
    valorUp: null,
    escala: 0.5,
    faixas: [{ ateUps: null, minimo: 0.5, maximo: 6 }],
  },
  [PLANO.CD_CPFL]: {
    valorUp: VALOR_UP[PLANO.CD_CPFL],
    escala: 0.1,
    faixas: [
      { ateUps: 1, minimo: 0, maximo: 3 },
      { ateUps: 4, minimo: 0, maximo: 4 },
      { ateUps: 8.33, minimo: 0, maximo: 6 },
      { ateUps: null, minimo: 0, maximo: 8 },
    ],
  },
  [PLANO.CD2_ELETROPAULO]: {
    valorUp: VALOR_UP[PLANO.CD2_ELETROPAULO],
    // TODO: confirmar a escala do CD2 Eletropaulo (não informada na regra; assumido 0,01).
    escala: 0.01,
    faixas: [
      { ateUps: 1, minimo: 0.5, maximo: 2.5 },
      { ateUps: 3.5, minimo: 0.5, maximo: 3.6 },
      { ateUps: null, minimo: 0.5, maximo: 4.5 },
    ],
  },
};

/** Busca a regra pelo id do plano. */
function regraDoPlano(planoId: number | null | undefined): RegraPlano | undefined {
  return planoId == null ? undefined : REGRAS_CONTRIBUICAO_BASICA[planoId];
}

/** Indica se o salário influencia os limites do plano. */
export function regraDependeDoSalario(planoId: number | null | undefined): boolean {
  return regraDoPlano(planoId)?.valorUp != null;
}

/** Retorna mínimo, máximo e escala da Contribuição Básica para o plano e salário informados. */
export function limitesContribuicaoBasica(
  planoId: number | null | undefined,
  salario: number,
): LimitesContribuicao | null {
  const regra = regraDoPlano(planoId);
  if (!regra) {
    return null;
  }
  const faixa =
    regra.valorUp == null
      ? regra.faixas[0]
      : (regra.faixas.find(
          (f) =>
            f.ateUps === null ||
            arredondarCentavos(salario) <= arredondarCentavos(f.ateUps * regra.valorUp!),
        ) ?? regra.faixas[regra.faixas.length - 1]);
  return { minimo: faixa.minimo, maximo: faixa.maximo, escala: regra.escala };
}

/**
 * Ajusta um percentual às regras: arredonda para o múltiplo mais próximo da escala
 * e limita ao intervalo [mínimo, máximo].
 */
export function ajustarPercentual(valor: number, limites: LimitesContribuicao): number {
  const passos = Math.round(arredondarCentavos(valor / limites.escala));
  const arredondado = arredondarCentavos(passos * limites.escala);
  return Math.min(limites.maximo, Math.max(limites.minimo, arredondado));
}

/** Compara percentuais ignorando ruído de ponto flutuante. */
export function mesmoPercentual(a: number, b: number): boolean {
  return Math.abs(a - b) < 0.000001;
}

function arredondarCentavos(valor: number): number {
  return Math.round((valor + Number.EPSILON) * 100) / 100;
}
